import React from 'react';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepButton from '@mui/material/StepButton';
import StepLabel from '@mui/material/StepLabel';
import Box from '@mui/material/Box';

import { useImportWizardStore } from '../store/useImportWizardStore';
import { WizardStep } from '../utils/types';

/**
 * Ordered list of wizard steps with their display labels.
 */
const WIZARD_STEPS: { key: WizardStep; label: string }[] = [
  { key: 'upload', label: 'Upload' },
  { key: 'analysis', label: 'Analysis' },
  { key: 'mapping', label: 'Mapping' },
  { key: 'validation', label: 'Validation' },
  { key: 'progress', label: 'Import' },
  { key: 'results', label: 'Results' },
];

/**
 * Returns the index of the given WizardStep in the ordered step list.
 */
function getStepIndex(step: WizardStep): number {
  const index = WIZARD_STEPS.findIndex((s) => s.key === step);
  return index >= 0 ? index : 0;
}

/**
 * WizardStepper displays the current progress through the import wizard.
 * Completed steps (before the current step) are clickable to navigate back.
 * Forward steps are not clickable.
 */
const WizardStepper: React.FC = () => {
  const currentStep = useImportWizardStore((s) => s.currentStep);
  const setStep = useImportWizardStore((s) => s.setStep);

  const activeStepIndex = getStepIndex(currentStep);

  const handleStepClick = (index: number) => {
    // Only allow navigating to completed (previous) steps
    if (index < activeStepIndex) {
      setStep(WIZARD_STEPS[index].key);
    }
  };

  return (
    <Box
      role="navigation"
      aria-label="Import wizard progress"
      data-testid="wizard-stepper"
    >
      <Stepper activeStep={activeStepIndex} alternativeLabel>
        {WIZARD_STEPS.map((step, index) => {
          const isCompleted = index < activeStepIndex;
          const isCurrent = index === activeStepIndex;

          return (
            <Step key={step.key} completed={isCompleted}>
              {isCompleted ? (
                <StepButton
                  onClick={() => handleStepClick(index)}
                  aria-label={`Go back to ${step.label} step`}
                  aria-current={isCurrent ? 'step' : undefined}
                >
                  {step.label}
                </StepButton>
              ) : (
                <StepLabel aria-current={isCurrent ? 'step' : undefined}>
                  {step.label}
                </StepLabel>
              )}
            </Step>
          );
        })}
      </Stepper>
    </Box>
  );
};

export default WizardStepper;
